import { useState, useMemo, useCallback } from 'react';
import { Alert, Chip, Spinner, Text } from '@billda/design-system';
import { usePlatformOrganisationFeatures } from '../../hooks/usePlatformOrganisationFeatures';
import { OrgFeatureSwitchDto } from '../../api/platformApi';
import {
  useSetOrgOverride,
  useRemoveOrgOverride,
} from '../../hooks/useFeatureOverrideMutations';
import FeatureToggleConfirmDialog, {
  FeatureToggleAction,
  OverrideState,
} from '../FeatureToggleConfirmDialog';
import { usePermission, Permissions } from '@billda/permissions-ui';
import { PageToolbar } from '../../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../../shared/components/ui/ChipTabs';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';

type SourceFilter = 'all' | 'org' | 'global' | 'default';

const SOURCE_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Org Override', value: 'org' },
  { label: 'Global', value: 'global' },
  { label: 'Default', value: 'default' },
] as const;

interface OrgFeaturesTabProps {
  orgId: string;
}

function getEffectiveChip(feature: OrgFeatureSwitchDto) {
  if (feature.source === 'default') {
    return { label: 'Default (off)', color: 'default' as const };
  }
  if (feature.effectiveState === 'enabled') {
    return { label: 'Enabled', color: 'success' as const };
  }
  return { label: 'Disabled', color: 'error' as const };
}

function toOrgCurrentState(feature: OrgFeatureSwitchDto): OverrideState {
  if (feature.source === 'default') return 'default';
  return feature.effectiveState === 'enabled' ? 'enabled' : 'disabled';
}

function getOrgFallback(feature: OrgFeatureSwitchDto): {
  state: OverrideState;
  source: string;
} {
  // When removing an org override, feature falls back to global or default.
  // We don't know the global override state from the DTO, so describe the source.
  if (feature.source === 'org') {
    return { state: 'default', source: 'global override or default' };
  }
  return { state: 'default', source: 'default' };
}

function getOrgAction(
  feature: OrgFeatureSwitchDto,
  orgName: string
): FeatureToggleAction {
  const currentState = toOrgCurrentState(feature);
  if (feature.source === 'org') {
    const fallback = getOrgFallback(feature);
    return {
      type: 'remove',
      scope: 'org',
      orgName,
      currentState,
      fallbackState: fallback.state,
      fallbackSource: fallback.source,
    };
  }
  return { type: 'set', scope: 'org', orgName, currentState };
}

export const OrgFeaturesTab = ({ orgId }: OrgFeaturesTabProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [selectedFeature, setSelectedFeature] =
    useState<OrgFeatureSwitchDto | null>(null);

  const { data, isLoading, error } = usePlatformOrganisationFeatures(orgId);

  const canManageFeatures = usePermission(Permissions.Features.Manage);

  const setOrgOverride = useSetOrgOverride();
  const removeOrgOverride = useRemoveOrgOverride();

  const isMutating = setOrgOverride.isPending || removeOrgOverride.isPending;

  const organisationName = data?.organisationName ?? '';
  const features = useMemo(() => data?.features ?? [], [data?.features]);

  const filteredFeatures = useMemo(() => {
    let items = features;
    if (sourceFilter !== 'all') {
      items = items.filter(f => f.source === sourceFilter);
    }
    if (searchValue) {
      const search = searchValue.toLowerCase();
      items = items.filter(
        f =>
          f.key.toLowerCase().includes(search) ||
          f.description.toLowerCase().includes(search)
      );
    }
    return items;
  }, [features, sourceFilter, searchValue]);

  const handleRowClick = useCallback(
    (feature: OrgFeatureSwitchDto) => {
      if (!canManageFeatures) {
        return;
      }
      setSelectedFeature(feature);
    },
    [canManageFeatures]
  );

  const handleConfirm = useCallback(
    (isEnabled: boolean) => {
      if (!selectedFeature || !orgId) return;

      const action = getOrgAction(selectedFeature, organisationName);
      const onSuccess = () => setSelectedFeature(null);

      if (action.type === 'remove') {
        removeOrgOverride.mutate(
          { key: selectedFeature.key, orgId },
          { onSuccess }
        );
      } else {
        setOrgOverride.mutate(
          { key: selectedFeature.key, orgId, isEnabled },
          { onSuccess }
        );
      }
    },
    [
      selectedFeature,
      orgId,
      organisationName,
      setOrgOverride,
      removeOrgOverride,
    ]
  );

  const handleCancel = useCallback(() => {
    setSelectedFeature(null);
  }, []);

  if (isLoading && !data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="mb-4">
        {error.message}
      </Alert>
    );
  }

  return (
    <div>
      <PageToolbar
        tabs={
          <ChipTabs
            tabs={[...SOURCE_TABS]}
            value={sourceFilter}
            onChange={v => setSourceFilter(v as SourceFilter)}
            data-testid="source-filter-tabs"
          />
        }
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />

      <ListViewShell
        itemCount={filteredFeatures.length}
        loading={isLoading}
        emptyMessage="No feature switches found"
        data-testid="org-features-list"
        columnHeaders={
          <>
            <Text variant="caption" color="secondary" className="flex-1">
              Feature
            </Text>
            <Text
              variant="caption"
              color="secondary"
              className="hidden w-[120px] text-right sm:block"
            >
              Effective State
            </Text>
          </>
        }
      >
        {filteredFeatures.map(feature => (
          <div
            key={feature.key}
            data-testid="org-feature-row"
            onClick={() => handleRowClick(feature)}
            className={`flex items-center gap-3 border-b border-border-default px-4 py-3 sm:px-6 ${
              canManageFeatures
                ? 'cursor-pointer hover:bg-surface-raised'
                : 'cursor-default'
            }`}
          >
            <div className="min-w-0 flex-1">
              <Text
                as="span"
                variant="body2"
                noWrap
                className="block font-semibold"
              >
                {feature.key}
              </Text>
              <Text
                as="span"
                variant="caption"
                color="secondary"
                noWrap
                className="block"
              >
                {feature.description}
              </Text>
              {feature.source === 'org' && (
                <Text
                  as="span"
                  variant="caption"
                  className="block text-[0.65rem] font-semibold text-content-info"
                >
                  Org override
                </Text>
              )}
            </div>

            <div className="flex w-[120px] justify-end">
              <Chip
                label={getEffectiveChip(feature).label}
                color={getEffectiveChip(feature).color}
                size="sm"
                className="text-[0.7rem]"
              />
            </div>
          </div>
        ))}
      </ListViewShell>

      {selectedFeature && (
        <FeatureToggleConfirmDialog
          open={!!selectedFeature}
          featureKey={selectedFeature.key}
          featureDescription={selectedFeature.description}
          action={getOrgAction(selectedFeature, organisationName)}
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};
